import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Spinner, CheckCircle, Printer, Truck, Wrench, ArrowRight, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

const SERVICE_META = {
  print_ship: { icon: Truck, title: "Print & Ship", price: "$1.00 / label", note: "+ $7.95–$19.95 shipping & handling per shipment" },
  print_install: { icon: Printer, title: "Print & Install", price: "$3.99 / asset", note: "Label + on-site installation" },
  onsite_volume: { icon: Wrench, title: "Large-Volume On-Site", price: "$2.99–$3.49 / asset", note: "Volume pricing for fleets & multi-location rollouts" },
};
const TIMELINES = ["As soon as possible", "Within 2 weeks", "Within 30 days", "Next quarter", "Just exploring"];
const EMPTY = { first_name: "", last_name: "", work_email: "", phone: "", company: "", service: "print_ship", label_count: "", stock: "", install_address: "", locations: "1", timeline: "", message: "", website: "" };
const money = (n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function RequestQuote() {
  const [form, setForm] = useState(EMPTY);
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  useEffect(() => { api.get("/quote-options").then(({ data }) => setStocks(data.stocks)).catch(() => {}); }, []);

  const needsAddress = form.service !== "print_ship";
  const count = Number(form.label_count) || 0;
  const est = form.service === "print_ship" ? [count * 1 + 7.95, count * 1 + 19.95] : form.service === "print_install" ? [count * 3.99, count * 3.99] : [count * 2.99, count * 3.49];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.work_email || !form.company || !count || !form.stock) return toast.error("Please fill in all required fields");
    if (needsAddress && !form.install_address.trim()) return toast.error("Please enter the installation address");
    setLoading(true);
    try {
      const { data } = await api.post("/quote-request", { ...form, label_count: count, locations: Number(form.locations) || 1 });
      setDone(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFB] text-zinc-950" data-testid="quote-page">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/orbit-icon.jpg" alt="Orbit" className="h-9 w-9 object-contain rounded-md" />
            <span className="font-display font-bold text-sm tracking-tight"><span className="sm:hidden">Orbit</span><span className="hidden sm:inline">Orbit Inventory Management</span></span>
          </Link>
          <Link to="/pricing"><Button variant="ghost" className="h-10 font-semibold gap-2" data-testid="quote-back"><ArrowLeft size={16} weight="bold" /> Pricing</Button></Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-12 lg:py-20 grid lg:grid-cols-2 gap-12 items-start">
        <div className="lg:sticky lg:top-24">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-600 mb-3">Label & Installation Services</p>
          <h1 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">Get a label service quote</h1>
          <p className="mt-4 text-lg text-zinc-600 leading-relaxed max-w-md">Tell us how many labels you need, the stock you want and where to install. We'll reply with a firm quote, usually within one business day.</p>
          <div className="mt-8 space-y-3">
            {Object.entries(SERVICE_META).map(([k, m]) => (
              <button type="button" key={k} onClick={() => setForm((f) => ({ ...f, service: k }))}
                className={`w-full text-left flex gap-3 p-4 rounded-xl border transition-colors ${form.service === k ? "border-emerald-500 bg-emerald-50/60 ring-1 ring-emerald-500" : "border-zinc-200 bg-white hover:border-zinc-300"}`}
                data-testid={`quote-service-${k}`} aria-pressed={form.service === k}>
                <div className="h-10 w-10 rounded-lg bg-white border border-zinc-200 flex items-center justify-center shrink-0"><m.icon size={20} weight="bold" className="text-emerald-600" /></div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-2"><p className="font-semibold">{m.title}</p><span className="text-sm font-bold text-emerald-700">{m.price}</span></div>
                  <p className="text-sm text-zinc-500">{m.note}</p>
                </div>
              </button>
            ))}
          </div>
          {count > 0 && (
            <div className="mt-6 rounded-xl border border-zinc-200 bg-white p-4" data-testid="quote-estimate">
              <p className="text-xs uppercase tracking-wider font-semibold text-zinc-500">List-price estimate</p>
              <p className="font-display text-2xl font-extrabold mt-1">{est[0] === est[1] ? money(est[0]) : `${money(est[0])} – ${money(est[1])}`}</p>
              <p className="text-xs text-zinc-500 mt-1">{count.toLocaleString()} labels · final quote may vary by stock, volume and travel.</p>
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-zinc-200 bg-white p-6 lg:p-8 shadow-xl shadow-zinc-200/50" data-testid="quote-form-card">
          {done ? (
            <div className="py-10 text-center" data-testid="quote-confirmation">
              <CheckCircle size={56} weight="fill" className="text-emerald-500 mx-auto" />
              <h2 className="font-display text-2xl font-bold mt-4">Quote request received!</h2>
              <p className="text-zinc-600 mt-2 max-w-sm mx-auto">Thanks, {form.first_name}. We'll send a firm quote for <b>{done.service_name}</b> to {form.work_email} shortly.</p>
              <p className="text-sm text-zinc-500 mt-3">List-price estimate: <b data-testid="quote-confirm-estimate">{done.estimate_low === done.estimate_high ? money(done.estimate_low) : `${money(done.estimate_low)} – ${money(done.estimate_high)}`}</b></p>
              <Link to="/pricing"><Button className="mt-6 h-12 px-6 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="quote-done">Back to Pricing</Button></Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
                <label>Website<input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} /></label>
              </div>
              <h2 className="font-display text-xl font-bold">Your label order</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold">Number of labels *</Label>
                  <Input type="number" min={1} value={form.label_count} onChange={set("label_count")} placeholder="e.g. 500" className="h-11 mt-1.5" data-testid="quote-label-count" />
                </div>
                <div>
                  <Label className="text-sm font-semibold">Locations</Label>
                  <Input type="number" min={1} value={form.locations} onChange={set("locations")} className="h-11 mt-1.5" data-testid="quote-locations" />
                </div>
              </div>
              <div>
                <Label className="text-sm font-semibold">Label stock *</Label>
                <Select value={form.stock} onValueChange={(v) => setForm((f) => ({ ...f, stock: v }))}>
                  <SelectTrigger className="h-11 mt-1.5" data-testid="quote-stock"><SelectValue placeholder="Choose a label stock…" /></SelectTrigger>
                  <SelectContent>{stocks.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-semibold">{needsAddress ? "Installation address *" : "Ship-to address"} <span className="font-normal text-zinc-400">{needsAddress ? "" : "(optional)"}</span></Label>
                <Textarea value={form.install_address} onChange={set("install_address")} rows={2} placeholder="Street, city, state, ZIP" className="mt-1.5" data-testid="quote-address" />
              </div>
              <div>
                <Label className="text-sm font-semibold">Timeline</Label>
                <Select value={form.timeline} onValueChange={(v) => setForm((f) => ({ ...f, timeline: v }))}>
                  <SelectTrigger className="h-11 mt-1.5" data-testid="quote-timeline"><SelectValue placeholder="When do you need them?" /></SelectTrigger>
                  <SelectContent>{TIMELINES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <h2 className="font-display text-xl font-bold pt-2">Contact</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-sm font-semibold">First Name *</Label><Input value={form.first_name} onChange={set("first_name")} className="h-11 mt-1.5" data-testid="quote-first-name" /></div>
                <div><Label className="text-sm font-semibold">Last Name *</Label><Input value={form.last_name} onChange={set("last_name")} className="h-11 mt-1.5" data-testid="quote-last-name" /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-sm font-semibold">Work Email *</Label><Input type="email" value={form.work_email} onChange={set("work_email")} placeholder="you@company.com" className="h-11 mt-1.5" data-testid="quote-email" /></div>
                <div><Label className="text-sm font-semibold">Phone <span className="font-normal text-zinc-400">(optional)</span></Label><Input value={form.phone} onChange={set("phone")} className="h-11 mt-1.5" data-testid="quote-phone" /></div>
              </div>
              <div><Label className="text-sm font-semibold">Company *</Label><Input value={form.company} onChange={set("company")} className="h-11 mt-1.5" data-testid="quote-company" /></div>
              <div><Label className="text-sm font-semibold">Notes <span className="font-normal text-zinc-400">(optional)</span></Label><Textarea value={form.message} onChange={set("message")} rows={3} placeholder="Asset types, label sizes, special requirements…" className="mt-1.5" data-testid="quote-message" /></div>
              <Button type="submit" disabled={loading} className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold gap-2" data-testid="quote-submit">
                {loading ? <Spinner size={18} className="animate-spin" /> : <>Request My Quote <ArrowRight size={18} weight="bold" /></>}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
